import dayjs, { Dayjs } from 'dayjs'

import {
  DEFAULT_DURATION_MS,
  INITIAL_TIME,
  MS_PER_DAY,
  TIME_SLOT_STEP_MS,
} from '../consts'

export const isScheduleTimeUnset = (value: number): boolean => (
  !Number.isFinite(value) || value === INITIAL_TIME
)

export const getScheduleTimeOffset = (value: number): number => (
  isScheduleTimeUnset(value) ? 0 : value
)

export const msToTimeLabel = (value: number): string => {
  if (isScheduleTimeUnset(value)) {
    return ''
  }

  const totalMinutes = Math.floor(((value % MS_PER_DAY) + MS_PER_DAY) % MS_PER_DAY / (60 * 1000))
  const hours = String(Math.floor(totalMinutes / 60)).padStart(2, '0')
  const minutes = String(totalMinutes % 60).padStart(2, '0')
  return `${hours}:${minutes}`
}

export const isValidTimeLabel = (label: string): boolean => {
  const match = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(label.trim())
  if (!match) {
    return false
  }

  const hours = Number(match[1])
  const minutes = Number(match[2])
  return (
    Number.isInteger(hours)
    && Number.isInteger(minutes)
    && hours >= 0
    && hours <= 23
    && minutes >= 0
    && minutes <= 59
  )
}

export const timeLabelToMs = (label: string): number => {
  if (!isValidTimeLabel(label)) {
    return INITIAL_TIME
  }

  const match = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(label.trim())
  if (!match) {
    return INITIAL_TIME
  }

  const hours = Number(match[1])
  const minutes = Number(match[2])
  return (hours * 60 + minutes) * 60 * 1000
}

export const buildTimeSlotOptions = (stepMs: number = TIME_SLOT_STEP_MS) => {
  const slotsCount = Math.floor(MS_PER_DAY / stepMs)
  return Array.from({ length: slotsCount }, (_, idx) => {
    const value = idx * stepMs
    return {
      label: msToTimeLabel(value),
      value,
    }
  })
}

export const getNextTimeSlotOffset = (
  now: Dayjs = dayjs().tz(),
  stepMs: number = TIME_SLOT_STEP_MS,
): number => {
  const minutesTotal = now.hour() * 60 + now.minute()
  const stepMinutes = stepMs / (60 * 1000)
  const nextSlotIdx = Math.floor(minutesTotal / stepMinutes) + 1
  const maxIdx = Math.floor(MS_PER_DAY / stepMs) - 1
  return Math.min(nextSlotIdx, maxIdx) * stepMs
}

export const getDefaultScheduleTimeRange = (
  now: Dayjs = dayjs().tz(),
  stepMs: number = TIME_SLOT_STEP_MS,
  durationMs: number = DEFAULT_DURATION_MS,
): { timeFrom: number; timeTo: number } => {
  const timeFrom = getNextTimeSlotOffset(now, stepMs)
  const timeTo = (timeFrom + durationMs) % MS_PER_DAY
  return {
    timeFrom,
    timeTo: timeTo === timeFrom
      ? (timeFrom + stepMs) % MS_PER_DAY
      : timeTo,
  }
}

export const ensureTimeToDiffers = (
  timeFrom: number,
  timeTo: number,
  stepMs: number = TIME_SLOT_STEP_MS,
): number => {
  if (isScheduleTimeUnset(timeFrom) || isScheduleTimeUnset(timeTo)) {
    return timeTo
  }

  if (timeFrom !== timeTo) {
    return timeTo
  }

  return (timeFrom + stepMs) % MS_PER_DAY
}

/** Длительность «с»–«по»; переход через полночь (timeTo < timeFrom) считается в пределах суток. */
export const getScheduleDurationMs = (
  timeFrom: number,
  timeTo: number,
): number => {
  if (isScheduleTimeUnset(timeFrom) || isScheduleTimeUnset(timeTo)) {
    return DEFAULT_DURATION_MS
  }

  if (timeTo >= timeFrom) {
    return timeTo - timeFrom
  }

  return MS_PER_DAY - timeFrom + timeTo
}

/** Новый конец при смене начала: прежняя длительность, 0 → DEFAULT_DURATION. */
export const shiftEndKeepingDuration = (
  previousFrom: number,
  nextFrom: number,
  timeTo: number,
  fallbackDurationMs: number = DEFAULT_DURATION_MS,
): number => {
  if (isScheduleTimeUnset(nextFrom)) {
    return timeTo
  }

  let durationMs = getScheduleDurationMs(previousFrom, timeTo)
  if (durationMs === 0) {
    durationMs = fallbackDurationMs
  }

  const nextTo = (nextFrom + durationMs) % MS_PER_DAY
  if (nextTo === nextFrom) {
    return (nextFrom + fallbackDurationMs) % MS_PER_DAY
  }

  return nextTo
}

export const buildOnceScheduleStart = (
  startDate: number,
  timeFrom: number,
  timezone: string,
): Dayjs | null => {
  if (!startDate || isScheduleTimeUnset(timeFrom)) {
    return null
  }

  return dayjs.tz(startDate, timezone).startOf('day').add(timeFrom, 'millisecond')
}

/** True when a one-time event start (date + timeFrom) is already in the past. */
export const isOnceScheduleStartInPast = (
  startDate: number,
  timeFrom: number,
  now: Dayjs,
  timezone: string,
): boolean => {
  const scheduledStart = buildOnceScheduleStart(startDate, timeFrom, timezone)
  if (!scheduledStart) {
    return false
  }

  return scheduledStart.isBefore(now)
}
